"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useState } from "react";
import { sanitizeDecimalString } from "@/lib/billing/quantity";
import { cx } from "@/lib/tailwind-utils";
type QuantityStepperSize = "default" | "small";
interface QuantityStepperProps {
  value: number;
  onChange: (n: number) => void;
  min?: number;
  max?: number;
  className?: string;
  ariaLabel?: string;
  disabled?: boolean;
  // "small" fits the stepper inside a dense row caption alongside body text.
  size?: QuantityStepperSize;
  // When true, the text field accepts decimals (e.g. 1.5) — for use cases
  // like medication quantity where staff enter fractional units.
  allowDecimal?: boolean;
}
const QuantityStepper: React.FC<QuantityStepperProps> = t0 => {
  const $ = _c(58);
  const {
    value,
    onChange,
    min: t1,
    max,
    className,
    ariaLabel,
    disabled: t2,
    size: t3,
    allowDecimal: t4
  } = t0;
  const min = t1 === undefined ? 1 : t1;
  const disabled = t2 === undefined ? false : t2;
  const size = t3 === undefined ? "default" : t3;
  const allowDecimal = t4 === undefined ? false : t4;
  const isSmall = size === "small";
  const controlAriaLabel = ariaLabel ?? "quantity";
  const inputAriaLabel = ariaLabel ?? "Quantity";
  const atMin = value <= min;
  const atMax = max !== undefined && value >= max;
  const [draft, setDraft] = useState(String(value));
  let t5;
  let t6;
  if ($[0] !== value) {
    t5 = () => {
      setDraft(String(value));
    };
    t6 = [value];
    $[0] = value;
    $[1] = t5;
    $[2] = t6;
  } else {
    t5 = $[1];
    t6 = $[2];
  }
  useEffect(t5, t6);
  let t7;
  if ($[3] !== allowDecimal) {
    t7 = raw => {
      if (!allowDecimal) {
        return raw.replace(/[^0-9]/g, "");
      }
      return sanitizeDecimalString(raw);
    };
    $[3] = allowDecimal;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  const sanitizeDraft = t7;
  let t8;
  if ($[5] !== allowDecimal || $[6] !== draft || $[7] !== max || $[8] !== min || $[9] !== onChange || $[10] !== value) {
    t8 = () => {
      const parsed = allowDecimal ? parseFloat(draft) : parseInt(draft, 10);
      if (!Number.isFinite(parsed)) {
        setDraft(String(value));
        return;
      }
      const clamped = max !== undefined ? Math.min(max, Math.max(min, parsed)) : Math.max(min, parsed);
      if (clamped !== value) {
        onChange(clamped);
      }
      setDraft(String(clamped));
    };
    $[5] = allowDecimal;
    $[6] = draft;
    $[7] = max;
    $[8] = min;
    $[9] = onChange;
    $[10] = value;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const commit = t8;
  const t9 = isSmall ? "rounded-md text-xs" : "text-sm";
  let t10;
  if ($[12] !== className || $[13] !== t9) {
    t10 = cx("inline-flex items-center rounded-xl border border-border bg-background text-foreground shadow-sm", t9, className);
    $[12] = className;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const t11 = isSmall ? "h-6 w-6 rounded-l-md" : "h-9 w-9 rounded-l-xl";
  let t12;
  if ($[15] !== t11) {
    t12 = cx("flex cursor-pointer items-center justify-center border-r border-border transition-colors duration-150 hover:bg-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-40", t11);
    $[15] = t11;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== min || $[18] !== onChange || $[19] !== value) {
    t13 = () => onChange(Math.max(min, value - 1));
    $[17] = min;
    $[18] = onChange;
    $[19] = value;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  const t14 = disabled || atMin;
  const t15 = `Decrease ${controlAriaLabel}`;
  let t16;
  if ($[21] !== t12 || $[22] !== t13 || $[23] !== t14 || $[24] !== t15) {
    t16 = <button type="button" className={t12} onClick={t13} disabled={t14} aria-label={t15}>−</button>;
    $[21] = t12;
    $[22] = t13;
    $[23] = t14;
    $[24] = t15;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  const t17 = allowDecimal ? "decimal" : "numeric";
  const t18 = allowDecimal ? "[0-9.]*" : "[0-9]*";
  let t19;
  if ($[26] !== sanitizeDraft) {
    t19 = e => setDraft(sanitizeDraft(e.target.value));
    $[26] = sanitizeDraft;
    $[27] = t19;
  } else {
    t19 = $[27];
  }
  let t20;
  if ($[28] !== value) {
    t20 = e_1 => {
      if (e_1.key === "Enter") {
        e_1.preventDefault();
        e_1.currentTarget.blur();
      } else {
        if (e_1.key === "Escape") {
          e_1.preventDefault();
          setDraft(String(value));
          e_1.currentTarget.blur();
        }
      }
    };
    $[28] = value;
    $[29] = t20;
  } else {
    t20 = $[29];
  }
  const t21 = isSmall ? "w-6 text-xs" : "w-10 text-sm";
  let t22;
  if ($[30] !== t21) {
    t22 = cx("border-0 bg-transparent p-0 text-center tabular-nums shadow-none outline-none ring-0 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:opacity-40", t21);
    $[30] = t21;
    $[31] = t22;
  } else {
    t22 = $[31];
  }
  let t23;
  if ($[32] !== commit || $[33] !== disabled || $[34] !== draft || $[35] !== inputAriaLabel || $[36] !== t17 || $[37] !== t18 || $[38] !== t19 || $[39] !== t20 || $[40] !== t22) {
    t23 = <input type="text" inputMode={t17} pattern={t18} value={draft} disabled={disabled} onChange={t19} onFocus={_temp} onBlur={commit} onKeyDown={t20} aria-label={inputAriaLabel} className={t22} />;
    $[32] = commit;
    $[33] = disabled;
    $[34] = draft;
    $[35] = inputAriaLabel;
    $[36] = t17;
    $[37] = t18;
    $[38] = t19;
    $[39] = t20;
    $[40] = t22;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  const t24 = isSmall ? "h-6 w-6 rounded-r-md" : "h-9 w-9 rounded-r-xl";
  let t25;
  if ($[42] !== t24) {
    t25 = cx("flex cursor-pointer items-center justify-center border-l border-border transition-colors duration-150 hover:bg-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-40", t24);
    $[42] = t24;
    $[43] = t25;
  } else {
    t25 = $[43];
  }
  let t26;
  if ($[44] !== max || $[45] !== onChange || $[46] !== value) {
    t26 = () => onChange(max !== undefined ? Math.min(max, value + 1) : value + 1);
    $[44] = max;
    $[45] = onChange;
    $[46] = value;
    $[47] = t26;
  } else {
    t26 = $[47];
  }
  const t27 = disabled || atMax;
  const t28 = `Increase ${controlAriaLabel}`;
  let t29;
  if ($[48] !== t25 || $[49] !== t26 || $[50] !== t27 || $[51] !== t28) {
    t29 = <button type="button" className={t25} onClick={t26} disabled={t27} aria-label={t28}>+</button>;
    $[48] = t25;
    $[49] = t26;
    $[50] = t27;
    $[51] = t28;
    $[52] = t29;
  } else {
    t29 = $[52];
  }
  let t30;
  if ($[53] !== t10 || $[54] !== t16 || $[55] !== t23 || $[56] !== t29) {
    t30 = <div className={t10}>{t16}{t23}{t29}</div>;
    $[53] = t10;
    $[54] = t16;
    $[55] = t23;
    $[56] = t29;
    $[57] = t30;
  } else {
    t30 = $[57];
  }
  return t30;
};
export default QuantityStepper;
function _temp(e_0) {
  return e_0.currentTarget.select();
}